export type UserType = 'INTERNAL' | 'EXTERNAL' | 'BOTH';

export interface PermissionDefinition {
  code: string;
  userType: UserType;
}

/**
 * Liste statique des codes de permission et leur type d'utilisateur.
 * Utilisee par le store auth pour valider les permissions.
 * Note: les labels/descriptions avec wording dynamique restent dans outil-pro.
 */
const PERMISSION_DEFINITIONS: PermissionDefinition[] = [
  // Dashboard
  { code: 'dashboard.view_all', userType: 'BOTH' },
  { code: 'dashboard.view_contracts', userType: 'BOTH' },
  { code: 'dashboard.view_revenue', userType: 'BOTH' },
  { code: 'dashboard.view_sav_claims', userType: 'BOTH' },
  { code: 'dashboard.view_sav_amounts', userType: 'BOTH' },
  { code: 'dashboard.view_provision', userType: 'BOTH' },
  { code: 'dashboard.view_encaissements', userType: 'BOTH' },
  { code: 'dashboard.view_synthese', userType: 'BOTH' },
  { code: 'dashboard.view', userType: 'INTERNAL' },
  { code: 'dashboard.*', userType: 'INTERNAL' },
  { code: 'admin_dashboard.view', userType: 'INTERNAL' },
  // Societes
  { code: 'society.list', userType: 'BOTH' },
  { code: 'society.create', userType: 'BOTH' },
  { code: 'society.edit', userType: 'BOTH' },
  { code: 'society.view_dashboard', userType: 'BOTH' },
  { code: 'society.view_informations', userType: 'BOTH' },
  { code: 'society.view_conditions', userType: 'BOTH' },
  { code: 'society.view_logs', userType: 'INTERNAL' },
  { code: 'society.toggle_status', userType: 'BOTH' },
  { code: 'society.edit_infos', userType: 'BOTH' },
  { code: 'society.edit_sav', userType: 'BOTH' },
  { code: 'society.edit_documents', userType: 'BOTH' },
  { code: 'society.edit_config', userType: 'INTERNAL' },
  { code: 'society.edit_options', userType: 'BOTH' },
  { code: 'society.edit_bank', userType: 'BOTH' },
  { code: 'society.manage_presets', userType: 'INTERNAL' },
  { code: 'society.export', userType: 'INTERNAL' },
  { code: 'society.rib_analysis', userType: 'INTERNAL' },
  { code: 'society.*', userType: 'INTERNAL' },
  // Reparateurs
  { code: 'repairer.list', userType: 'INTERNAL' },
  { code: 'repairer.*', userType: 'INTERNAL' },
  // Utilisateurs internes
  { code: 'internal_user.list', userType: 'INTERNAL' },
  { code: 'internal_user.create', userType: 'INTERNAL' },
  { code: 'internal_user.edit', userType: 'INTERNAL' },
  { code: 'internal_user.edit_permissions', userType: 'INTERNAL' },
  { code: 'internal_user.delete', userType: 'INTERNAL' },
  { code: 'internal_user.*', userType: 'INTERNAL' },
  // Utilisateurs externes
  { code: 'external_user.list', userType: 'BOTH' },
  { code: 'external_user.view', userType: 'BOTH' },
  { code: 'external_user.create', userType: 'BOTH' },
  { code: 'external_user.edit_personal_info', userType: 'BOTH' },
  { code: 'external_user.edit_societies', userType: 'BOTH' },
  { code: 'external_user.edit_password', userType: 'BOTH' },
  { code: 'external_user.edit_permissions', userType: 'BOTH' },
  { code: 'external_user.promote', userType: 'INTERNAL' },
  { code: 'external_user.impersonate', userType: 'INTERNAL' },
  { code: 'external_user.view_audit_logs', userType: 'INTERNAL' },
  { code: 'external_user.resend_welcome_email', userType: 'BOTH' },
  { code: 'external_user.delete', userType: 'BOTH' },
  { code: 'external_user.export', userType: 'INTERNAL' },
  { code: 'external_user.*', userType: 'INTERNAL' },
  // Garanties
  { code: 'warranty.list', userType: 'BOTH' },
  { code: 'warranty.view', userType: 'BOTH' },
  { code: 'warranty.create', userType: 'BOTH' },
  { code: 'warranty.edit', userType: 'BOTH' },
  { code: 'warranty.edit_price', userType: 'BOTH' },
  { code: 'warranty.edit_color', userType: 'BOTH' },
  { code: 'warranty.reorder', userType: 'BOTH' },
  { code: 'warranty.duplicate', userType: 'BOTH' },
  { code: 'warranty.toggle_status', userType: 'BOTH' },
  { code: 'warranty.update_provisions', userType: 'BOTH' },
  { code: 'warranty.download_cgp', userType: 'BOTH' },
  { code: 'warranty.delete', userType: 'INTERNAL' },
  { code: 'warranty.view_diagnostic', userType: 'INTERNAL' },
  { code: 'warranty.manage_recommendations', userType: 'INTERNAL' },
  { code: 'warranty.*', userType: 'INTERNAL' },
  // Contrats
  { code: 'contract.list', userType: 'BOTH' },
  { code: 'contract.create', userType: 'BOTH' },
  { code: 'contract.view_all', userType: 'BOTH' },
  { code: 'contract.request_cancellation', userType: 'BOTH' },
  { code: 'contract.cancel', userType: 'INTERNAL' },
  { code: 'contract.offer', userType: 'INTERNAL' },
  { code: 'contract.use_test_data', userType: 'INTERNAL' },
  { code: 'contract.view_audit_logs', userType: 'INTERNAL' },
  { code: 'contract.recalc', userType: 'INTERNAL' },
  { code: 'contract.recreate_subscription', userType: 'INTERNAL' },
  { code: 'contract.*', userType: 'INTERNAL' },
  // Attestations de livraison
  { code: 'delivery_proof.list', userType: 'BOTH' },
  { code: 'delivery_proof.create', userType: 'BOTH' },
  { code: 'delivery_proof.view_all', userType: 'EXTERNAL' },
  { code: 'delivery_proof.*', userType: 'INTERNAL' },
  // SAV
  { code: 'sav.list', userType: 'BOTH' },
  { code: 'sav.view', userType: 'BOTH' },
  { code: 'sav.view_all', userType: 'BOTH' },
  { code: 'sav.manage', userType: 'BOTH' },
  { code: 'sav.sign', userType: 'BOTH' },
  { code: 'sav.export', userType: 'INTERNAL' },
  { code: 'sav.backoffice', userType: 'INTERNAL' },
  { code: 'sav.*', userType: 'INTERNAL' },
  // Administration du SAV. Categorie dediee : les COMMERCIAL portent `sav.*`.
  { code: 'sav_admin.audit', userType: 'INTERNAL' },
  { code: 'sav_admin.manage_tags', userType: 'INTERNAL' },
  { code: 'sav_admin.signature_tools', userType: 'INTERNAL' },
  { code: 'sav_admin.*', userType: 'INTERNAL' },
  // Factures
  { code: 'invoice.list', userType: 'BOTH' },
  { code: 'invoice.view_child_companies', userType: 'BOTH' },
  { code: 'invoice.admin', userType: 'INTERNAL' },
  { code: 'invoice.change_state', userType: 'INTERNAL' },
  { code: 'invoice.regenerate_pdf', userType: 'INTERNAL' },
  { code: 'invoice.rerun', userType: 'INTERNAL' },
  { code: 'invoice.credit_note', userType: 'INTERNAL' },
  { code: 'invoice.send_bank', userType: 'INTERNAL' },
  { code: 'invoice.send_email', userType: 'INTERNAL' },
  { code: 'invoice.download_billing', userType: 'INTERNAL' },
  { code: 'invoice.download_sepa', userType: 'INTERNAL' },
  { code: 'invoice.download_netcash', userType: 'INTERNAL' },
  { code: 'invoice.*', userType: 'INTERNAL' },
  // Tarifications catalogue
  { code: 'catalog_pricing.list', userType: 'INTERNAL' },
  { code: 'catalog_pricing.*', userType: 'INTERNAL' },
  // Frais de service
  { code: 'invoice_fees.list', userType: 'INTERNAL' },
  { code: 'invoice_fees.*', userType: 'INTERNAL' },
  // PLV
  { code: 'plv.list', userType: 'BOTH' },
  { code: 'plv.orders', userType: 'BOTH' },
  { code: 'plv.products', userType: 'BOTH' },
  { code: 'plv.templates', userType: 'INTERNAL' },
  { code: 'plv.create_template', userType: 'INTERNAL' },
  { code: 'plv.create_product', userType: 'INTERNAL' },
  { code: 'plv.*', userType: 'INTERNAL' },
  // Webhooks
  { code: 'webhooks.list', userType: 'INTERNAL' },
  { code: 'webhooks.*', userType: 'INTERNAL' },
  // Stripe
  { code: 'stripe.account', userType: 'BOTH' },
  { code: 'stripe.self', userType: 'BOTH' },
  // UpSell (plateforme commerciale interne)
  { code: 'upsell.access', userType: 'INTERNAL' },
  { code: 'upsell.dashboard', userType: 'INTERNAL' },
  { code: 'upsell.admin', userType: 'INTERNAL' },
  { code: 'upsell.*', userType: 'INTERNAL' },
  // Cles API publiques des societes clientes.
  // Categorie dediee (et non `society.manage_api_keys`) : le wildcard
  // `society.*` porte par les SUPERVISOR ouvrirait sinon la gestion des cles.
  { code: 'api_key.manage', userType: 'INTERNAL' },
  { code: 'api_key.*', userType: 'INTERNAL' },
  // Nouveautes produit (redaction et publication). Categorie dediee, comme
  // `api_key` : aucun wildcard de categorie existant ne doit l'accorder.
  { code: 'news.manage', userType: 'INTERNAL' },
  { code: 'news.*', userType: 'INTERNAL' },
  // Argent des mensualites d'un contrat (remboursement, report du 1er prelevement).
  // Categorie dediee : COMMERCIAL et SUPERVISOR portent `contract.*`.
  { code: 'contract_payment.postpone_first_charge', userType: 'INTERNAL' },
  { code: 'contract_payment.*', userType: 'INTERNAL' },
  // Bandeau du site de declaration de sinistre
  { code: 'declaration_banner.manage', userType: 'INTERNAL' },
  { code: 'declaration_banner.*', userType: 'INTERNAL' },
  // Controles d'integrite des donnees
  { code: 'integrity_check.run', userType: 'INTERNAL' },
  { code: 'integrity_check.*', userType: 'INTERNAL' },
];

/**
 * Verifie si une permission est valide pour un type d'utilisateur donne
 */
export const isPermissionValidForUserType = (
  permissionCode: string,
  userType: 'INTERNAL' | 'EXTERNAL'
): boolean => {
  const permission = PERMISSION_DEFINITIONS.find(p => p.code === permissionCode);
  if (!permission) return false;
  return permission.userType === 'BOTH' || permission.userType === userType;
};
